import { BriefStatus } from '@magicbrief/prisma/generated/client2';
import { BaseTranslation } from 'src/i18n/i18n-types';

const briefStatus: Record<BriefStatus, string> = {
  DRAFT: 'Draft',
  NEEDS_APPROVAL: 'Awaiting Approval',
  SHARED: 'Shared',
  FEEDBACK_PROVIDED: 'Feedback Provided',
  COMPLETE: 'Complete',
  ARCHIVED: 'Archived',
};

export const briefs: BaseTranslation = {
  multiSelect: {
    numSelected: '{count} brief{{count:s}} selected',
    addToCollections: 'Add {count} brief{{s}} to collections',
  },
  brief: 'Brief',
  briefs: 'Briefs',
  status: 'Status',
  type: 'Type',
  team: 'Team',
  content: 'Content',
  brands: 'Brands',
  profiles: 'Profiles',
  profile: 'Profile',
  concepts: 'Concepts',
  stage: 'Stage',
  due: 'Due',
  assignment: {
    assignmentEdit: 'Change assignees for {briefName}',
    assignees: 'Assignees',
    addAssignees: 'Add Assignees',
    additionalList: '+ {num} more',
    search: 'Search for assignees...',
  },
  brandProfiles: 'Brand Profiles',
  productProfiles: 'Product Profiles',
  addProduct: 'Add Product',
  addBrand: 'Add Brand',
  createBrandProfile: 'Create Brand Profile',
  createProductProfile: 'Create Product Profile',
  editBrandProfile: 'Edit Brand Profile',
  editProductProfile: 'Edit Product Profile',
  deleteBrandProfile: 'Delete Brand Profile',
  productCover: 'Product Cover Image',
  deleteBrandProfileDetail:
    'Are you sure you want to delete this brand profile? Any product profiles for this brand will also be deleted.',
  logoPreviewRestrictions:
    'Only JPEG, PNG, WebP, or AVIF images under 500 kB allowed.',
  emptyTitle: 'Nothing to see here',
  emptyBrand: 'Try creating a new brand profile to get started',
  emptyProducts: 'Try creating a new product profile to get started',
  emptyBrandProfilesList:
    'No brand profiles. Create a new brand profile under Profiles.',
  simpleStatuses: {
    all: 'All',
    active: 'Active',
    complete: 'Complete',
  },
  briefType: {
    ITERATION: 'Iteration',
    NET_NEW: 'Net New',
  },
  numProducts: '{count} product{{s}}',
  numBriefs: '{count} brief{{s}}',
  manage: 'Manage Brief',
  briefStatus,
  template: 'Template',
  templates: 'Templates',
  createCollection: 'Create Collection',
  createBrief: 'Create Brief',
  newBrief: 'New Brief',
  createBriefFromTemplate: 'Create Brief from Template "{name}"',
  createTemplateShort: 'Create Template',
  createTemplate: 'Create Template from Brief',
  deleteTemplates: 'Delete {{ Template | ?? Templates }}',
  createdTemplate: 'Template Created',
  duplicateBrief: 'Duplicate Brief',
  collection: 'Collection',
  collections: 'Collections',
  shareScreenReader: 'Sharing privacy setting for brief {name}',
  empty: {
    title: 'Create a brief in minutes',
    description:
      'Create context rich briefs in minutes using our smart modules, embed your saved ads or insights. ',
    actionLabel: 'Create Brief',
  },
  briefCollections: '{name} + {count} other{{count:s}}',
  deleteBriefs: 'Delete {{ Brief | ?? Briefs }}',
  deleteFromCollectionConfirm:
    'You can choose to remove this brief just from the collection "{name}", or you can delete it entirely.',
  deleteEntirelyDetail:
    "Deleting a brief will permanently delete it from both your and your team's briefs.",
  removeFromCollectionOnly: 'Only Remove From {collection}',
  deleteCollection: 'Delete Collection',
  deleteCollectionConfirm:
    "You can choose to remove this collection and preserve the briefs inside it, or you can delete it and it's associated briefs permanently. Any briefs in this collection that are also in other collections will be preserved in all cases.",

  deleteCollectionAndContents: 'Delete Collection & Delete Briefs',
  deleteCollectionOnly: 'Delete Collection & Preserve Briefs',
  share: {
    public: { description: 'Anyone with the link can view this brief.' },
    private: {
      description:
        'Only team members or users with access can view this brief.',
    },
  },
  alsoDeleteBriefs: 'Delete briefs in this collection',
  editor: {
    untitled: 'Untitled Brief',
    tools: {
      paragraph: {
        title: 'Paragraph',
        description: 'Insert a text block',
      },
      adReference: {
        title: 'Ad Reference',
        description: 'Add reference ads from your library',
      },
      channel: {
        title: 'Channel',
        description: 'Add channel information',
      },
      productInfo: {
        title: 'Product Info',
        description: 'Add product information',
        descLabel: 'About',
        descPlaceholder: 'Enter a product description here...',
        linkLabel: 'Product Page',
        linkPlaceholder: 'www.example.com',
      },
      checklist: {
        title: 'Checklist',
        description: 'Create a checklist',
      },
      header: {
        title: 'Header',
        description: 'Create a heading',
      },
      list: {
        title: 'List',
        description: 'Create a list',
      },
      table: {
        title: 'Table',
        description: 'Insert a table',
        addColumnBefore: 'Add column before',
        addColumnAfter: 'Add column after',
        deleteColumn: 'Delete column',
        addRowBefore: 'Add row before',
        addRowAfter: 'Add row after',
        deleteRow: 'Delete row',
      },
      assetUpload: {
        title: 'Media',
        description: 'Upload images and video',
      },
      fileUpload: {
        title: 'Attach Files',
        description: 'Attach other files',
      },
      targetAudience: {
        title: 'Target Audience',
        description: 'Add target audience information',
        descPlaceholder: 'Enter an audience description here',
      },
      outputSizes: {
        title: 'Output Sizes',
        description: 'Add output size details',
      },
      variants: {
        title: 'Variant Info',
        description: 'Add variant details',
        editPlaceholder: 'Enter alt details',
        timePlaceholder: 'Add time',
        variantName: 'Variant Name',
        targetTime: 'Target Time',
      },
      format: {
        title: 'Format',
        description: 'Add format information',
      },
      assetsLink: {
        title: 'Asset Links',
        description: 'Add links to assets',
        linkPlaceholder: 'Enter link',
        notesPlaceholder: 'Add notes',
        assetsLink: 'Asset Link',
        notes: 'Notes',
      },
      musicLink: {
        title: 'Music',
        description: 'Add music information',
        linkPlaceholder: 'Enter link',
        notesPlaceholder: 'Add notes',
        musicLink: 'Music Link',
        notes: 'Notes',
      },
      productFeatureList: {
        title: 'Features',
        description: 'Highlight features or ingredients',
        namePlaceholder: 'Enter name',
        descPlaceholder: 'Add description',
        name: 'Feature Name',
        desc: 'Description',
      },
      brandGuidelines: {
        title: 'Guidelines',
        linkPlaceholder: 'Add link to Figma, etc.',
        description: 'Include your brand guidelines',
      },
      style: {
        title: 'Content Style',
        description: 'Add content style',
      },
      clearFormat: {
        title: 'Clear Format',
      },
      taskList: {
        title: 'Task List',
      },
      orderedList: {
        title: 'Ordered List',
      },
      bulletList: {
        title: 'Bullet List',
      },
      bold: {
        title: 'Bold',
      },
      italic: {
        title: 'Italic',
      },
      strike: {
        title: 'Strike',
      },
      underline: {
        title: 'Underline',
      },
      highlight: {
        title: 'Highlight',
      },
      heading1: {
        title: 'Heading 1',
      },
      concepts: {
        title: 'Concepts',
        description: 'Add a concept from your workspace',
        descPlaceholder: 'Enter a concept here',
        addConcept: 'Add Concept',
      },
      link: {
        title: 'Link',
        prompt: 'Enter a link',
        invalid: 'Invalid link',
      },
      embed: {
        title: 'Embed',
        description: 'Embed a website, video, preview, and more',
        convertToLink: 'Convert to Link',
        convertToEmbed: 'Convert to Embed',
        error: "The embedded link couldn't be displayed: {detail}",
        preview: 'Preview',
        dialog:
          'Provide a URL to embed into the document. YouTube, Figma, Loom, Spotify, and hundreds more providers are supported.',
      },
      brandDetails: {
        title: 'Brand Details',
        description: 'Provide essential brand information',
        name: 'Brand Name',
        industry: 'Industry',
        industryPlaceholder: 'Select industry',
        namePlaceholder: 'Enter brand name',
        info: 'Brand Info',
        infoPlaceholder: 'Enter a brand description here',
        link: 'Brand Page',
      },
      productDetails: {
        title: 'Product Details',
        description: 'Provide essential product information',
        name: 'Product Name',
        category: 'Category',
        categoryPlaceholder: 'Select category',
        namePlaceholder: 'Enter product name',
        info: 'Product Summary',
        infoPlaceholder: 'Enter a product description here',
        link: 'Product Page',
      },
    },

    handleTooltip: 'Drag to move. Click to show menu.',
    disconnected: {
      title: 'Offline / Connection Error',
      detail:
        'The connection was lost. Check your network connection and refresh the page.',
    },
    readOnly: {
      title: 'Read Only',
      referenceAds: 'Reference Ads',
      media: 'Media',
      outputDetails: 'Output Details',
      channels: 'Channels',
      aspectRatios: 'Aspect Ratios',
      style: 'Style',
      formats: 'Formats',
      viewConcept: 'View Concept',
      productPage: 'Product Page',
      brandPage: 'Brand Page',
      table: 'Table',
    },
  },
  contentView: {
    authRequired: {
      title: 'Sign in to view and upload content',
      description:
        "You'll need to <>create an account<> or <>sign in<> to view and upload content for this brief.",
    },
    upload: {
      title: 'Upload files',
      success: 'Uploading files successful',
      error: 'Uploading files errored: {error}',
    },
  },
  errors: {
    save: 'Something went wrong while saving your brief',
    addToCollection:
      "Something went wrong and we couldn't add the brief to that collection",
    removeFromCollection:
      "Something went wrong and we weren't able to remove the brief from that collection",
    listBriefs: "We couldn't retrieve the list of your briefs",
    listCollections: "We couldn't retrieve the list of your brief collections",
    openFileAttachment:
      "We couldn't generate a link to that file. Try refreshing the page and contact support if the issue persists.",
  },
  searchPlaceholder: 'Search briefs by name...',
};
